import React, {
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {Fade} from "react-bootstrap";
import {createPortal} from "react-dom";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import "./_sql-table-header-filter-component.scss";
import Button from "../basics/button/Button";
import LabeledInput from "../basics/input/labeled-input/LabeledInput";
import {Formik, useField} from "formik";
import {FilterTypes} from "../../constans/filter";
import LabeledSelect from "../basics/select/labeled-select/LabeledSelect";
import Checkbox from "../basics/checkbox/Checkbox";
import sowState from "../../constans/sowState";
import {
    getFallReasons,
    getMedicines,
    getRacesData,
    getSuppliers,
} from "../../selectors/dictionarySelectors";
import {useSelector} from "react-redux";
import {textContrast} from "../../utils/ColorUtils";
import {useTranslation} from "react-i18next";
import {PARTURITION_DIFFICULTY} from "../../constans/eventTypes";
import {USG_STATE} from "@wesstron/utils/Api/constants/usgStates";
import {REMOVE_TECHNOLOGY_GROUP_REASONS} from "../../utils/TechnologyGroupUtils";

function StringFilter() {
    const {t} = useTranslation();

    const [input] = useField("value");

    return (
        <LabeledInput
            label={t("value")}
            type="text"
            {...input}
            value={input.value || ""}
            onChangeWithEvent
        />
    );
}

function BooleanFilter() {
    const {t} = useTranslation();

    const [input, , handlers] = useField("value");

    const options = useMemo(
        () => [
            {value: true, name: t("yes")},
            {value: false, name: t("no")},
        ],
        [t]
    );

    return (
        <LabeledSelect
            label={t("value")}
            value={input.value || ""}
            options={options}
            onChange={handlers.setValue}
            clearButton={false}
        />
    );
}

function DictionaryRow({name, label, color, checked, onChange: onChangeProps}) {
    const onChange = useCallback(
        (value) => {
            onChangeProps(value, name);
        },
        [name, onChangeProps]
    );

    return (
        <li>
            <Checkbox
                id={name}
                checked={checked}
                onChange={onChange}
                label=""
            />
            <span
                className={color ? "ps-1 pe-1 rounded" : null}
                style={
                    color
                        ? {background: color, color: textContrast(color)}
                        : null
                }>
                {label}
            </span>
        </li>
    );
}

function DictionaryComponent({items}) {
    const [input, , handlers] = useField("value");

    const isChecked = useCallback(
        (name) => {
            return input.value?.includes(name) ?? false;
        },
        [input.value]
    );

    const onChange = useCallback(
        (value, name) => {
            const copy = input.value?.slice() ?? [];
            if (value) {
                copy.push(name);
            } else {
                const index = copy.indexOf(name);
                if (index !== -1) {
                    copy.splice(index, 1);
                }
            }
            handlers.setValue(copy);
        },
        [input.value, handlers]
    );

    return (
        <ul className="sql-table-header-filter-component-dictionary">
            {items.map((item) => (
                <DictionaryRow
                    key={item.name}
                    name={item.name}
                    label={item.label}
                    color={item.color}
                    checked={isChecked(item.name)}
                    onChange={onChange}
                />
            ))}
        </ul>
    );
}

function MedicineFilter() {
    const medicines = useSelector(getMedicines);

    const items = useMemo(
        () =>
            medicines
                .map((medicine) => ({
                    name: medicine.WordID,
                    label: medicine.WData.Name,
                    color: medicine.WData.Color,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [medicines]
    );

    return <DictionaryComponent items={items} />;
}

function ParturitionLevelFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {name: t("low"), value: PARTURITION_DIFFICULTY.LOW},
            {
                name: t("medium"),
                value: PARTURITION_DIFFICULTY.MEDIUM,
            },
            {name: t("hard"), value: PARTURITION_DIFFICULTY.HARD},
        ],
        [t]
    );

    return <DictionaryComponent items={items} />;
}

function RaceFilter() {
    const races = useSelector(getRacesData);

    const items = useMemo(
        () =>
            races
                .map((race) => ({
                    name: race.ID,
                    label: race.Value,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [races]
    );

    return <DictionaryComponent items={items} />;
}

function RaceCodeFilter() {
    const races = useSelector(getRacesData);

    const items = useMemo(
        () =>
            races
                .map((race) => ({
                    name: race.ID,
                    label: race.Code,
                    color: race.Color,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [races]
    );

    return <DictionaryComponent items={items} />;
}

function FallReasonFilter() {
    const fallReasons = useSelector(getFallReasons);

    const items = useMemo(
        () =>
            fallReasons.WData.map((item) => ({
                name: item.ID,
                label: item.Value,
            })),
        [fallReasons]
    );

    return <DictionaryComponent items={items} />;
}

function USGResultFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {name: t("events.usgEvent.negative"), label: USG_STATE.NEGATIVE},
            {name: t("events.usgEvent.positive"), label: USG_STATE.POSITIVE},
            {name: t("events.usgEvent.toRepeat"), label: USG_STATE.REPEAT},
        ],
        [t]
    );

    return <DictionaryComponent items={items} />;
}

function SowStateFilter() {
    const {t} = useTranslation();

    const items = useMemo(() => {
        return Object.keys(sowState).map((key) => ({
            name: key,
            label: t(`sowStatus.${sowState[key]}`),
        }));
    }, [t]);

    return <DictionaryComponent items={items} />;
}

function SupplierFilter() {
    const suppliers = useSelector(getSuppliers);

    const items = useMemo(
        () =>
            suppliers.WData.map((item) => ({
                name: item.ID,
                label: item.Value,
            })),
        [suppliers]
    );

    return <DictionaryComponent items={items} />;
}

function TechnologyGroupRemovalFilter() {
    const {t} = useTranslation();

    const items = useMemo(
        () => [
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.INSEMINATION_OTHER_WEEK,
                label: t("inseminationInNextWeek"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.NEGATIVE_USG,
                label: t("animalDocuments.removeReason.negativeUSG"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PREGNANCY,
                label: t("events.parturitionEvent.noPregnancy"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.SEPARATION,
                label: t("eventTypes.S"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.FALL,
                label: t("eventTypes.F"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.SELL,
                label: t("eventTypes.L"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.NEXT_CYCLE,
                label: t("foundNextCycle"),
            },
            {
                name: REMOVE_TECHNOLOGY_GROUP_REASONS.NO_PARTURITION_EVENT,
                label: t("noParturitionEvent"),
            },
            // {name: REMOVE_TECHNOLOGY_GROUP_REASONS.IS_MOMMY, label: "Mamkowanie"},
        ],
        [t]
    );

    return <DictionaryComponent items={items} />;
}

function SQLTableFilterComponentForm({
    handleSubmit,
    resetForm,
    header,
    filterType,
}) {
    const {t} = useTranslation();

    const onResetClick = useCallback(() => {
        resetForm({values: {type: filterType}});
    }, [resetForm, filterType]);

    const Component = useMemo(() => {
        switch (header.filterType) {
            case FilterTypes.BOOLEAN:
                return BooleanFilter;
            case FilterTypes.MEDICINE:
                return MedicineFilter;
            case FilterTypes.PARTURITION_LEVEL:
                return ParturitionLevelFilter;
            case FilterTypes.RACE:
                return RaceFilter;
            case FilterTypes.RACE_CODE:
                return RaceCodeFilter;
            case FilterTypes.FALL_REASON:
                return FallReasonFilter;
            case FilterTypes.USG_RESULT:
                return USGResultFilter;
            case FilterTypes.SOW_STATE:
                return SowStateFilter;
            case FilterTypes.SUPPLIER:
                return SupplierFilter;
            case FilterTypes.TECHNOLOGY_GROUP_REMOVAL:
                return TechnologyGroupRemovalFilter;
            default:
                return StringFilter;
        }
    }, [header.filterType]);

    return (
        <>
            <Component />
            <div className="sql-table-header-filter-component-apply">
                <small>
                    <Button
                        buttonStyle="text"
                        className="me-3"
                        type="button"
                        onClick={onResetClick}>
                        {t("clear")}
                    </Button>
                </small>
                <Button buttonColor="success" onClick={handleSubmit}>
                    {t("apply")}
                </Button>
            </div>
        </>
    );
}

export default function SQLTableFilterComponent({
    show,
    parent,
    setShowFilter,
    filters,
    setFilters,
    header,
}) {
    const container = useRef(null);

    const filterType = useMemo(() => {
        if (
            [
                FilterTypes.STRING,
                FilterTypes.NUMBER,
                FilterTypes.DATE,
                FilterTypes.BOOLEAN,
            ].includes(header.filterType)
        )
            return header.filterType;
        return "dictionary";
    }, [header.filterType]);

    const initialValues = useMemo(() => {
        if (filters[header.field]) {
            return filters[header.field];
        }
        return {type: filterType};
    }, [header, filters, filterType]);

    const [style, setStyle] = useState({left: 0, top: 0});

    const onClickOutside = useCallback(() => {
        setShowFilter(false);
    }, [setShowFilter]);

    useOnClickOutside(container, onClickOutside);

    const setPositionForParent = useCallback(() => {
        if (!parent.current) return;
        const rect = parent.current.getBoundingClientRect();
        const {innerWidth, innerHeight} = window;
        const newStyle = {};
        if (rect.left > innerWidth / 2) {
            newStyle.right = innerWidth - rect.left - rect.width;
        } else {
            newStyle.left = rect.left;
        }
        if (rect.top > innerHeight / 2) {
            newStyle.bottom =
                innerHeight - rect.top - rect.height + window.scrollY;
        } else {
            newStyle.top = rect.top + rect.height + window.scrollY;
        }
        setStyle(newStyle);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        if (!show) return;
        setPositionForParent();
    }, [show, setPositionForParent]);

    const onSubmit = useCallback(
        (values, formikBag) => {
            console.log(values, formikBag);
            setFilters((prev) => {
                const copy = {...prev};
                if (values.value) {
                    copy[header.field] = values;
                } else {
                    delete copy[header.field];
                }
                return copy;
            });
            setShowFilter(false);
        },
        [setShowFilter, header, setFilters]
    );

    return createPortal(
        <Fade in={show} mountOnEnter unmountOnExit>
            <div
                className="sql-table-header-filter-component"
                style={{...style}}
                ref={container}>
                <Formik onSubmit={onSubmit} initialValues={initialValues}>
                    {(props) => (
                        <SQLTableFilterComponentForm
                            {...props}
                            header={header}
                            filterType={filterType}
                        />
                    )}
                </Formik>
            </div>
        </Fade>,
        document.getElementById("root")
    );
}
